import React, { useCallback, useEffect, useState } from 'react';
import { rateioService } from '../services/api';
import { fmtDateTime } from '../utils/formatters';
import { TONE_BADGE, TONE_BORDER, TONE_SURFACE, TONE_TEXT } from '../utils/statusColors';
import {
  EmpresaDoDryRun,
  EstadoDoMotor,
  RecusaDoLigar,
  ResumoDoDryRun,
  TEXTO_DO_MOTIVO,
  empresasDoDryRun,
  lerRecusaDoLigar,
  situacaoDaSimulacao,
} from '../utils/motorDoRateio';
import { useConfirm } from './shared/ConfirmDialog';
import { useAviso } from './shared/Aviso';
import { useRateio } from '../hooks/useRateio';
import { ComRateio } from '../utils/rateioDoGrupo';
import DeclararCompetenciaPassada from './DeclararCompetenciaPassada';

/**
 * MOTOR DO RATEIO: operação no grupo econômico (prévia aprovada pelo Heitor em
 * 04/10/2026). Fica abaixo do "Quem usa, quem paga".
 *
 * ⛔ Ao abrir, só LÊ o estado (`GET .../rateio/motor`). Simular é botão: carimba a
 * prova (`dryRunEm`) e por isso é ato do operador, nunca efeito de abrir a tela.
 * ⛔ Ligar só com `podeLigar`; com simulação vencida o botão nem aparece.
 * ⛔ A lista é por CONTRATO (`contractId`), agrupada por empresa só para somar.
 */

const brl = (centavos: number) => `R$ ${(centavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const reais = (v: number) => brl(Math.round(Number(v || 0) * 100));
const competenciaCorrente = () => {
  const d = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const ehAdmin = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}')?.role === 'admin'; } catch { return false; }
};

const Selo: React.FC<{ tom: keyof typeof TONE_BADGE; children: React.ReactNode }> = ({ tom, children }) => (
  <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${TONE_BADGE[tom]}`}>{children}</span>
);

const Empresa: React.FC<{ e: EmpresaDoDryRun }> = ({ e }) => (
  <div className='rounded-lg border border-slate-700 overflow-hidden' data-testid={`empresa-${e.clientId}`}>
    <div className='flex items-center gap-2 px-3 py-2 bg-slate-800/60 border-b border-slate-800'>
      <span className='text-sm font-medium text-white'>{e.nome}</span>
      <span className='font-mono text-xs text-slate-500'>#{e.clientId}</span>
      <span className='flex-1' />
      <Selo tom={e.aplicaria ? 'open' : 'warning'}>{e.aplicaria ? 'aplicaria' : 'não aplicaria'}</Selo>
    </div>
    {e.contratos.map((c) => (
      <div key={c.contractId} className='px-3 py-2 text-xs border-b border-slate-800 last:border-0 space-y-1'>
        <div className='flex flex-wrap gap-x-4 gap-y-1 text-slate-300 tabular-nums'>
          <span className='font-mono text-slate-500'>contrato {c.contractId}</span>
          <span>antes {reais(c.antes)}</span>
          <span>depois {reais(c.depois)}</span>
          <span className={c.deltaCentavos === 0 ? TONE_TEXT.open : TONE_TEXT.danger}>diferença {brl(c.deltaCentavos)}</span>
        </div>
        {c.aplicaria ? (
          <>
            <div className='text-slate-400'>
              Cota emitida: {c.linhasDeCota.map((l) => `${l.productCode} ${reais(l.totalValue)}`).join(' · ') || '—'}
              {' · '}suprimidos {c.suprimidos.length} item(ns) {reais(c.suprimidos.reduce((s, i) => s + Number(i.totalValue || 0), 0))}
            </div>
            {c.limpeza.renomeados.map((r) => (
              <div key={`${c.contractId}-${r.productId}`} className='text-slate-500'>
                <span className='line-through'>{r.de}</span> → <span className='text-slate-300'>{r.para}</span>
              </div>
            ))}
            {(c.limpeza.removidos > 0 || !c.limpeza.mrrNaoSeMove) && (
              <div className={TONE_TEXT.danger}>A limpeza removeria item ou mexeria no valor: não ligue.</div>
            )}
          </>
        ) : (
          <div className={TONE_TEXT.warning}>{(c.motivo && TEXTO_DO_MOTIVO[c.motivo]) || c.detalhe || 'Não aplicaria.'}</div>
        )}
      </div>
    ))}
    <div className={`px-3 py-1.5 text-xs border-t border-slate-800 ${e.suprimidoCentavos === e.emitidoCentavos ? TONE_TEXT.open : TONE_TEXT.danger}`}>
      Balanço: suprimido {brl(e.suprimidoCentavos)} {e.suprimidoCentavos === e.emitidoCentavos ? '=' : '≠'} emitido {brl(e.emitidoCentavos)}
    </div>
  </div>
);

export default function PainelDoMotor({ clientId, nome = (id) => `#${id}` }: { clientId: number; nome?: (clientId: number) => string }) {
  const confirmar = useConfirm();
  const avisar = useAviso();
  const [estado, setEstado] = useState<EstadoDoMotor | null>(null);
  const [oculto, setOculto] = useState(!ehAdmin());
  const [competencia, setCompetencia] = useState(competenciaCorrente());
  const [ocupado, setOcupado] = useState<null | 'simular' | 'ligar' | 'desligar'>(null);
  const [recusa, setRecusa] = useState<RecusaDoLigar | null>(null);
  const [declarando, setDeclarando] = useState(false);
  // Mesmo cache do "Quem usa, quem paga": produtos, critério e fatias vigentes, que
  // são o ponto de partida da declaração de competência passada.
  const { rateio } = useRateio(oculto ? null : clientId);

  const ler = useCallback(async () => {
    try {
      setEstado(await rateioService.estadoDoMotor(clientId));
    } catch (e: any) {
      // Não-admin (403) ou rota indisponível: o painel some, a tela do grupo segue.
      setOculto(true);
    }
  }, [clientId]);

  useEffect(() => { if (!oculto) ler(); }, [ler, oculto]);

  if (oculto || !estado || estado.temRateio === false) return null;

  const { motor, dryRun } = estado;
  const situacao = situacaoDaSimulacao(dryRun.vencido);
  const resumo: ResumoDoDryRun | null = dryRun.resumo;
  const empresas = resumo ? empresasDoDryRun(resumo.faturas) : [];

  const simular = async () => {
    setOcupado('simular');
    setRecusa(null);
    try {
      await rateioService.simularMotor(clientId, competencia);
      await ler();
    } catch (e: any) {
      avisar('Não foi possível simular: ' + (e?.response?.data?.mensagem || e?.response?.data?.message || e?.message), 'error');
    } finally { setOcupado(null); }
  };

  const ligar = async () => {
    const ok = await confirmar({
      title: 'Ligar o motor do rateio?',
      message: 'A geração de fatura deste grupo passa a emitir a cota de cada empresa como linha própria.',
      consequence: 'Numa transação só: tira o "(rateio X%)" do nome dos itens do pool nos contratos ativos e liga o motor. Nenhum valor muda.',
      confirmLabel: 'Ligar',
    });
    if (!ok) return;
    setOcupado('ligar');
    setRecusa(null);
    try {
      await rateioService.ligarMotor(clientId);
      avisar('Motor ligado.', 'success');
      await ler();
    } catch (e: any) {
      const r = lerRecusaDoLigar(e);
      if (r) setRecusa(r);
      else avisar('Não foi possível ligar: ' + (e?.response?.data?.message || e?.message), 'error');
    } finally { setOcupado(null); }
  };

  // Desligar não pede simulação nem confirmação: voltar ao comportamento conhecido
  // é sempre permitido.
  const desligar = async () => {
    setOcupado('desligar');
    try {
      await rateioService.desligarMotor(clientId);
      avisar('Motor desligado.', 'success');
      await ler();
    } catch (e: any) {
      avisar('Não foi possível desligar: ' + (e?.response?.data?.message || e?.message), 'error');
    } finally { setOcupado(null); }
  };

  const textoDaSimulacao =
    situacao === 'nunca' ? 'Nunca simulado.'
      : `Simulação de ${resumo?.competencia || '—'}${resumo?.rodadoPor ? ` por ${resumo.rodadoPor}` : ''}${dryRun.em ? `, ${fmtDateTime(dryRun.em)}` : ''}.`;

  return (
    <section className='rounded-xl border border-slate-700 bg-slate-900 overflow-hidden mt-4' aria-label='Motor do rateio'>
      <div className='flex items-start justify-between gap-3 px-4 py-3 border-b border-slate-800'>
        <div className='min-w-0'>
          <h3 className='text-sm font-semibold text-white'>Motor do rateio</h3>
          <p className='font-mono text-xs text-slate-400 mt-0.5'>
            {motor.ativo
              ? `ligado${motor.ativoPorNome ? ` por ${motor.ativoPorNome}` : ''}${motor.ativoEm ? ` em ${fmtDateTime(motor.ativoEm)}` : ''}`
              : 'desligado: a fatura cobra o valor gravado no item do contrato'}
          </p>
        </div>
        <div className='flex gap-2 shrink-0'>
          <Selo tom={motor.ativo ? 'open' : 'neutral'}>{motor.ativo ? 'Ligado' : 'Desligado'}</Selo>
          {situacao === 'valida' && <Selo tom={resumo?.todasAplicariam ? 'open' : 'warning'}>{resumo?.todasAplicariam ? 'Simulação válida' : 'Simulação com recusa'}</Selo>}
          {situacao === 'vencida' && <Selo tom='warning'>Simulação vencida</Selo>}
        </div>
      </div>

      <div className='px-4 py-3 space-y-3 text-sm'>
        <p className='text-xs text-slate-400'>
          {textoDaSimulacao}
          {situacao === 'vencida' && ' Algo mudou depois dela (contrato, item, nome ou versão): simule de novo antes de ligar.'}
        </p>

        {recusa && (
          <div className={`rounded-lg border p-3 space-y-1 ${recusa.tom === 'danger' ? `${TONE_SURFACE.danger} ${TONE_BORDER.danger}` : `${TONE_SURFACE.warning} ${TONE_BORDER.warning}`}`} role='alert'>
            <p className={`font-medium ${recusa.tom === 'danger' ? TONE_TEXT.danger : TONE_TEXT.warning}`}>{recusa.titulo}</p>
            <p className='text-slate-200 text-xs'>{recusa.texto}</p>
            {recusa.empresas && recusa.empresas.length > 0 && (
              <ul className='text-xs text-slate-300 list-disc pl-4'>
                {recusa.empresas.map((emp) => (
                  <li key={emp.clientId}>{emp.nome}: {(emp.motivo && TEXTO_DO_MOTIVO[emp.motivo]) || emp.detalhe}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {situacao !== 'nunca' && resumo && (
          <>
            <div className='flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400'>
              <span>Todas aplicariam: <b className={resumo.todasAplicariam ? TONE_TEXT.open : TONE_TEXT.warning}>{resumo.todasAplicariam ? 'sim' : 'não'}</b></span>
              <span>Total muda: <b className={resumo.mudariaTotal ? TONE_TEXT.danger : TONE_TEXT.open}>{resumo.mudariaTotal ? 'sim' : 'não'}</b></span>
              <span>Limpeza move MRR: <b className={resumo.limpezaMoveriaMrr ? TONE_TEXT.danger : TONE_TEXT.open}>{resumo.limpezaMoveriaMrr ? 'sim' : 'não'}</b></span>
              <span>Renomeações: <b className='text-slate-200'>{resumo.renomeacoes}</b></span>
            </div>
            <div className='space-y-2'>
              {empresas.map((e) => <Empresa key={e.clientId} e={e} />)}
            </div>
          </>
        )}

        {motor.ativo && (
          <p className='text-xs text-slate-500'>
            Desligar volta a fatura ao comportamento de antes, sem pedir simulação. Os nomes continuam sem o "(rateio X%)", o que é inofensivo: a limpeza nunca mexeu em valor.
          </p>
        )}
      </div>

      <div className='flex flex-wrap items-center gap-2 px-4 py-3 border-t border-slate-800'>
        {rateio && rateio.temRateio !== false && (
          <button type='button' onClick={() => setDeclarando(true)} className='px-3 py-1.5 rounded-lg border border-slate-600 bg-slate-800 text-sm text-slate-200 hover:bg-slate-700'>
            Declarar competência passada
          </button>
        )}
        <label className='text-xs text-slate-400' htmlFor={`motor-comp-${clientId}`}>Competência</label>
        <input
          id={`motor-comp-${clientId}`}
          type='month'
          value={competencia}
          onChange={(e) => setCompetencia(e.target.value)}
          className='px-2 py-1.5 bg-slate-950 border border-slate-700 rounded-lg text-sm text-white'
        />
        <button type='button' onClick={simular} disabled={!!ocupado || !competencia} className='px-3 py-1.5 rounded-lg border border-slate-600 bg-slate-800 text-sm text-slate-200 hover:bg-slate-700 disabled:opacity-50'>
          {ocupado === 'simular' ? 'Simulando…' : situacao === 'nunca' ? 'Simular' : 'Simular de novo'}
        </button>
        <span className='flex-1' />
        {motor.ativo ? (
          <button type='button' onClick={desligar} disabled={!!ocupado} className='px-3 py-1.5 rounded-lg border border-slate-600 bg-slate-800 text-sm text-slate-200 hover:bg-slate-700 disabled:opacity-50'>
            {ocupado === 'desligar' ? 'Desligando…' : 'Desligar o motor'}
          </button>
        ) : situacao !== 'vencida' && (
          <>
            {!dryRun.podeLigar && <span className='text-xs text-slate-500'>{situacao === 'nunca' ? 'Rode a simulação primeiro' : 'Resolva as recusas para ligar'}</span>}
            <button type='button' onClick={ligar} disabled={!dryRun.podeLigar || !!ocupado} className='px-3 py-1.5 rounded-lg bg-primary hover:bg-primary-strong text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed'>
              {ocupado === 'ligar' ? 'Ligando…' : 'Ligar o motor'}
            </button>
          </>
        )}
      </div>
      {declarando && rateio && rateio.temRateio !== false && (
        <DeclararCompetenciaPassada
          rateio={rateio as ComRateio}
          nome={nome}
          onClose={() => setDeclarando(false)}
          onDeclarado={() => { setDeclarando(false); avisar('Competência declarada.', 'success'); ler(); }}
        />
      )}
    </section>
  );
}
